import type { EventEmitter } from 'node:events'
import { readFileSync } from 'node:fs'
import { Readable, Transform } from 'node:stream'
import { SAXParser, SaxEventType, type Tag } from 'sax-wasm'
import { type Entry, Parse } from 'unzipper'
import * as dto from './dto'

export type GarItem = InstanceType<
  (typeof SLUG_DTO_MAP)[keyof typeof SLUG_DTO_MAP]
>

export interface GarTransformOptions {
  filter?(file: string): boolean
}

export class GarTransformError extends Error {
  constructor(
    readonly file: string,
    readonly error: unknown,
    readonly attributes: Readonly<Record<string, string>>,
    readonly tag: Tag,
  ) {
    super()
  }
}

const wasmUrl = new URL(import.meta.resolve('sax-wasm/lib/sax-wasm.wasm'))
const saxWasm = readFileSync(wasmUrl)

class GarTransform extends Transform {
  private readonly zipParser = Parse({ forceStream: true })
  private readonly entryTasks: Promise<void>[] = []

  constructor(readonly options: GarTransformOptions = {}) {
    super({
      writableObjectMode: false,
      readableObjectMode: true,

      transform: (chunk, encoding, callback) =>
        this.zipParser.write(chunk, encoding, callback),

      flush: callback => this.zipParser.end(callback),
    })

    this.zipParser
      .on('data', entry =>
        this.entryTasks.push(
          this.handleFile(entry).catch(error => {
            this.destroy(error)
          }),
        ),
      )
      .on('error', error => this.destroy(error))
      .on('end', async () => {
        try {
          await Promise.all(this.entryTasks)
          this.push(null)
        } catch (error) {
          this.destroy(error as Error)
        }
      })
  }

  async handleFile(entry: Entry) {
    const saxParser = new SAXParser(SaxEventType.OpenTag)

    await saxParser.prepareWasm(saxWasm)

    if (this.options.filter && !this.options.filter(entry.path)) {
      entry.autodrain()
      return
    }

    const slug = PATH_REGEXP.exec(entry.path)?.groups?.slug?.toLowerCase()

    if (!slug || !isValidSlug(slug)) {
      entry.autodrain()
      return
    }

    const Dto = SLUG_DTO_MAP[slug]
    const stream = Readable.toWeb(entry)
    const reader = stream.getReader() as ReadableStreamDefaultReader<
      Uint8Array<ArrayBufferLike>
    >

    for await (const [event, tag] of saxParser.parse(reader)) {
      if (event !== SaxEventType.OpenTag || !tag.selfClosing) {
        continue
      }

      const attributes = tag.attributes.reduce<Record<string, string>>(
        (acc, { name, value }) => {
          acc[name.value] = value.value
          return acc
        },
        {},
      )

      if (Object.keys(attributes).length === 0) {
        continue
      }

      try {
        this.push(
          new Dto({
            filePath: entry.path,
            attributes,
          }),
        )
      } catch (error) {
        throw new GarTransformError(entry.path, error, attributes, tag)
      }
    }
  }
}

interface GarTransformEmitter
  extends AsyncIterable<GarItem>,
    EventEmitter<
      Readonly<{
        close: []
        data: [gerItem: GarItem]
        drain: []
        end: []
        error: [error: Error | GarTransformError]
        finish: []
        pause: []
        pipe: [src: Readable]
        readable: []
        resume: []
        unpipe: [src: Readable]
      }>
    > {}

const GarExport = GarTransform as unknown as {
  new (
    ...args: ConstructorParameters<typeof GarTransform>
  ): Omit<
    GarTransform,
    | 'on'
    | 'once'
    | 'off'
    | 'addListener'
    | 'prependListener'
    | 'prependOnceListener'
    | 'removeListener'
    | symbol
  > &
    GarTransformEmitter
}

export { GarExport as GarTransform }

const PATH_REGEXP = /^(?:\d+\/)?as_(?<slug>[^\d]+)_\d.*\.xml/i

const SLUG_DTO_MAP = {
  addr_obj: dto.AddressObject,
  addr_obj_division: dto.AddressObjectDivision,
  addr_obj_params: dto.AddressObjectParam,
  adm_hierarchy: dto.AdmHierarchyItem,
  apartments: dto.Apartment,
  apartments_params: dto.ApartmentParam,
  carplaces: dto.CarPlace,
  carplaces_params: dto.CarPlaceParam,
  change_history: dto.ChangeHistoryItem,
  houses: dto.House,
  houses_params: dto.HouseParam,
  mun_hierarchy: dto.MunHierarchyItem,
  normative_docs: dto.NormDoc,
  reestr_objects: dto.RegistryObjectsItem,
  rooms: dto.Room,
  rooms_params: dto.RoomParam,
  steads: dto.Stead,
  steads_params: dto.SteadParam,

  addhouse_types: dto.AdditionalHouseType,
  addr_obj_types: dto.AddressObjectType,
  apartment_types: dto.ApartmentType,
  house_types: dto.HouseType,
  normative_docs_kinds: dto.NormDocKind,
  normative_docs_types: dto.NormDocType,
  object_levels: dto.ObjectLevel,
  operation_types: dto.OperationType,
  param_types: dto.ParamType,
  room_types: dto.RoomType,
} as const

const isValidSlug = (slug: string): slug is keyof typeof SLUG_DTO_MAP =>
  Object.hasOwn(SLUG_DTO_MAP, slug)
